import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { AxiosInstance } from 'axios';
import { SegmentEntity } from './types';
import { Auth } from '../auth/types';
import SegmentsApi from '../../api/segmentsApi';
import { showToast } from '../../aurora/components/Toast/Toast';
import { RootState } from '../store';
import { JourneyEntity } from '../journeys/types';
import { SegmentSnapshotEntity } from '../segmentSnapshots/types';
import { downloadBlob } from '../../utils/downloadFile';

export type SegmentToSnapshotsMap = Record<
  SegmentEntity['_id'],
  SegmentSnapshotEntity[]
>;

export interface SegmentState {
  snapshotsBySegment: SegmentToSnapshotsMap;
  segmentById: Record<SegmentEntity['_id'], SegmentEntity | undefined>;
}

const initialState: SegmentState = {
  snapshotsBySegment: {},
  segmentById: {},
};

interface BulkCsvPayload {
  axios: AxiosInstance;
  auth: Auth | null;
  journeys: JourneyEntity[];
}
export const downloadCsvAction = createAsyncThunk(
  'segments/downloadCsv',
  ({
    axios,
    journeys,
    fullLog,
    isArtycViewer,
  }: BulkCsvPayload & { fullLog: boolean; isArtycViewer: boolean }) => {
    // dedupe: two selected journeys pointing at the same segment would be
    // counted twice against a zip that can only ever hold one entry for it
    const segmentIds = Array.from(
      new Set(
        journeys
          .filter((journey) => journey.segmentId !== undefined)
          .map((journey) => journey.segmentId!)
      )
    );

    if (fullLog) {
      return SegmentsApi.downloadLogCsv(axios, isArtycViewer, segmentIds);
    }
    // For customer version exports, always use the company endpoint
    // even if the viewer is an Artyc user.
    const useArtycEndpoint = false;
    return SegmentsApi.downloadSegmentCsv(
      axios,
      useArtycEndpoint,
      // assumes all journeys have the same company
      journeys[0]?.company,
      segmentIds
    );
  }
);

const segmentSlice = createSlice({
  name: 'segments',
  initialState,
  reducers: {
    setSnapshots: (state, action: PayloadAction<SegmentToSnapshotsMap>) => {
      state.snapshotsBySegment = {
        ...state.snapshotsBySegment,
        ...action.payload,
      };
    },
    setSegment: (state, action: PayloadAction<SegmentEntity>) => {
      state.segmentById = {
        ...state.segmentById,
        ...{ [action.payload._id]: action.payload },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(downloadCsvAction.pending, () => {
      showToast({
        type: 'info',
        title: 'Downloading',
        text: 'Preparing your files for download. Please wait...',
      });
    });

    builder.addCase(downloadCsvAction.fulfilled, (state, action) => {
      // The server returns a single CSV when exactly one segment is exported
      // and a zip otherwise, so derive the extension from the request rather
      // than the blob's MIME type: Safari reports it inconsistently (e.g.
      // "text/csv; charset=utf-8"), which previously mislabeled single CSVs as
      // "shipments.zip". Pass the original blob to the Safari-safe helper.
      const journeysWithSegment = action.meta.arg.journeys.filter(
        (journey) => journey.segmentId !== undefined
      );
      const requested = new Set(
        journeysWithSegment.map((journey) => journey.segmentId!)
      ).size;

      let filename: string;
      if (requested === 1) {
        const { serialNumber, pid } = journeysWithSegment[0];
        const logType = action.meta.arg.fullLog ? 'full_log' : 'shipment';

        filename = `${serialNumber}-${logType}-${pid}.csv`;
      } else {
        // customer facing, still best to name it shipments
        filename = 'shipments.zip';
      }

      downloadBlob(action.payload.blob, filename);

      // Selected journeys without a synced segment can't be exported and are
      // dropped before the request. Call them out by pid so the user knows the
      // zip is intentionally smaller than their selection (SOF-2049).
      const { journeys } = action.meta.arg;
      const skipped = journeys.filter((j) => j.segmentId === undefined);
      const skippedNote =
        skipped.length > 0
          ? `\n${skipped.length} skipped — no data yet: ${skipped
              .map((j) => j.pid)
              .join(', ')}`
          : '';

      // Report what the server actually put in the file rather than the size of
      // the selection: shipments the server couldn't resolve or read are gone
      // from the zip either way, and claiming otherwise is how a short download
      // went unnoticed (SOF-2059). Older servers omit the header — fall back to
      // the requested count so the message never regresses.
      const downloaded = action.payload.exportedCount ?? requested;
      const missing = requested - downloaded;
      const missingNote =
        missing > 0
          ? `\n${missing} could not be exported — see EXPORT_REPORT.txt in the download.`
          : '';

      showToast({
        type: missing > 0 ? 'info' : 'success',
        title: missing > 0 ? 'Partial download' : 'Success!',
        text: `Downloaded ${downloaded} shipment${
          downloaded === 1 ? '' : 's'
        }.${missingNote}${skippedNote}\nPlease check your downloads folder.`,
      });
    });

    builder.addCase(downloadCsvAction.rejected, () => {
      showToast({
        type: 'error',
        title: 'Uh oh...',
        text: 'There was an error preparing your files. \nPlease try again or contact support if it continues.',
      });
    });
  },
});

export const selectSnapshotsBySegment = (state: RootState) =>
  state.segments.snapshotsBySegment;

export const { setSnapshots, setSegment } = segmentSlice.actions;

export default segmentSlice.reducer;
